'use client'

import { useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

/**
 * Componente LoadingSpinner
 * @description Muestra un spinner de carga.
 * @returns {JSX.Element} Spinner de carga.
 */
export const LoadingSpinner = () => {
  // This code assumes 'useState', 'useEffect', and 'useMemo' are available (usually imported from 'react').
  // Crucially, it also assumes a 'useDebounce' hook is available in the scope of this component.
  // This hook could be imported from a library (e.g., 'use-debounce') or defined elsewhere in your project file.
  // Example of a custom useDebounce hook:
  // const useDebounce = (value, delay) => {
  //   const [debouncedValue, setDebouncedValue] = useState(value);
  //   useEffect(() => {
  //     const handler = setTimeout(() => setDebouncedValue(value), delay);
  //     return () => clearTimeout(handler);
  //   }, [value, delay]);
  //   return debouncedValue;
  // };

  const MESSAGES = useMemo(
    () => [
      '🚀 Preparando tu experiencia inmersiva...',
      '💾 Optimizando caché para un despegue veloz...',
      '✨ Renderizando el futuro, pixel a pixel...',
      '🌍 Conectando a la nueva dimensión... ¡Casi list@!',
      '⏳ Un instante más... ¡La magia está por desplegarse!'
    ],
    []
  )

  // This state represents an index that changes at a regular interval.
  const [rawMessageIndex, setRawMessageIndex] = useState(0)

  // Interval at which the rawMessageIndex attempts to change.
  const RAW_INDEX_UPDATE_INTERVAL_MS = 2000 // e.g., attempts to change every 2 seconds

  // Debounce delay. The displayed message will update this long after rawMessageIndex changes.
  const DEBOUNCE_DELAY_MS = 500 // e.g., UI updates 0.5 seconds after rawMessageIndex changes

  useEffect(() => {
    if (MESSAGES.length === 0) return

    const timer = setInterval(() => {
      setRawMessageIndex(prevIndex => (prevIndex + 1) % MESSAGES.length)
    }, RAW_INDEX_UPDATE_INTERVAL_MS)

    return () => clearInterval(timer) // Cleanup interval on component unmount
  }, [MESSAGES, RAW_INDEX_UPDATE_INTERVAL_MS]) // Dependencies for the effect

  // The debouncedMessageIndex will follow rawMessageIndex, but its updates will be delayed
  // by DEBOUNCE_DELAY_MS. This fulfills the "using useDebounce" requirement.
  const debouncedMessageIndex = useDebounce(rawMessageIndex, DEBOUNCE_DELAY_MS)

  const currentMessage =
    MESSAGES.length > 0 ? MESSAGES[debouncedMessageIndex % MESSAGES.length] : 'Cargando contenido...'

  return (
    <div role='status' aria-live='polite' className='flex flex-col items-center'>
      <svg
        className='animate-spin h-8 w-8 text-gray-500' // Existing spinner animation
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
      >
        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v8H4z'></path>
      </svg>
      <p
        key={currentMessage} // Using the message string as a key can help if you add CSS transitions for text changes
        className='mt-2 text-gray-500 text-center px-4 min-h-[4em] flex items-center justify-center w-full max-w-xs sm:max-w-sm' // Adjusted width and height for text
      >
        {currentMessage}
      </p>
    </div>
  )
}
export default LoadingSpinner
